import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import { ConfirmRemoveButton } from "../primitives/ConfirmRemoveButton";
import type { EditorStyles } from "../ProgramEditor/styles";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";
import type { PlanStyles } from "./planComposerStyles";
import type { Composer } from "./useComposer";
import { ComposerSetTable } from "./ComposerSetTable";
import { rangeLabel } from "./composeBlocks";

// The expanded, edit-in-place editor for ONE program exercise (solo or
// superset) — the program twin of PlanExerciseEditor. It renders where the
// exercise sits (the accordion row on narrow, the detail pane on wide)
// instead of in a compose-form at the top, killing the select-at-bottom →
// edit-at-top scroll churn. The body is the proven compose machinery moved
// verbatim out of the old top ComposeForm: the weekly-ramp segment strip,
// the shared ComposerSetTable, the D/W placement chips, and the explicit
// Update/ADD commit (ramp needs per-segment commit + the blocked-switch-
// while-dirty guard — untouched). Around it: a head (title + ▾ collapse)
// and a two-step ✕ remove (the DayPanel idiom), never colour-only.
export function ProgramExerciseEditor({
  composer,
  onReplaceMember,
  resolveName,
  cs,
  ps,
  selectStyles,
  theme,
  onCollapse,
  onClone,
  onRemove,
}: {
  composer: Composer;
  onReplaceMember: (memberIdx: number) => void;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  cs: ComposerStyles;
  ps: PlanStyles;
  selectStyles: EditorStyles;
  theme: UiTheme;
  onCollapse: () => void;
  onClone: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const {
    form,
    weekCount,
    canCommit,
    removeMember,
    updateRow,
    setMemberKind,
    setMemberInstructions,
    addRound,
    removeRound,
    toggleDay,
    toggleWeek,
    commit,
    resetForm,
    dayCount,
    formConflict,
    cellUnit,
  } = composer;
  const { formDirty, ramp, rampBlocks, rampWeeks, weekEdit, selectRampSegment, setRampPerWeek } =
    composer;
  const editing = form.editing !== null;

  const inRamp =
    ramp !== null &&
    rampBlocks !== null &&
    (rampBlocks.length > 1 || (rampWeeks?.length ?? 0) > 1);
  const perWeek = inRamp && ramp!.perWeek;
  // ⧉ Clone only when the entry is a SINGLE block (uniform across its weeks).
  // A true week-ramp (multiple segments) has no whole-ramp clone model yet —
  // clone a segment via ✎ instead (same limit as the old DayPanel ⧉).
  const cloneable = editing && (ramp === null || (rampBlocks?.length ?? 0) === 1);

  const names = form.members.map((m) => resolveName(m.exerciseId, m.exerciseName));
  const title = form.grouped ? names.join(" + ") : names[0] ?? "";

  // CD-1 — an edit opened from a day's panel is day-scoped: Update writes only
  // that day (tick more D-chips to widen), never the block's sibling days. The
  // banners say so; the day number comes from the form's editingDay.
  const editDay = (form.editingDay ?? 0) + 1;
  const banner =
    weekEdit !== null && inRamp && !rampWeeks!.includes(weekEdit)
      ? t("composer.addingWeek", {
          n: weekEdit + 1,
          defaultValue: "New week W{{n}} — Update adds it to this exercise",
        })
      : weekEdit !== null
        ? t("composer.editingWeekDay", {
            n: weekEdit + 1,
            d: editDay,
            defaultValue: "Editing W{{n}} on D{{d}} only — Update breaks it out of its segment",
          })
        : inRamp
          ? t("composer.editingRampDay", {
              d: editDay,
              defaultValue: "Weekly ramp on D{{d}} — Update applies to the selected weeks, this day only",
            })
          : t("composer.editingBlockDay", {
              d: editDay,
              defaultValue: "Editing D{{d}} — Update applies to this day (tick more days to apply there too)",
            });

  return (
    <View style={ps.editorFrame}>
      <View style={ps.editorHead}>
        <Text style={ps.editorTitle} numberOfLines={1}>
          {title}
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: true }}
          accessibilityLabel={t("plans.collapseExercise", { defaultValue: "Collapse exercise" })}
          onPress={onCollapse}
          hitSlop={8}
          style={ps.collapseBtn}
        >
          <Icon name="chevron-down" size={ps.collapseText.fontSize} color={ps.collapseText.color} />
        </Pressable>
      </View>

      {editing ? (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Icon name="edit" size={cs.editingBanner.fontSize} color={cs.editingBanner.color} />
          <Text style={cs.editingBanner}>{banner}</Text>
        </View>
      ) : null}

      {inRamp ? (
        <View style={{ gap: 4 }}>
          <View style={cs.chipsLine}>
            <Text style={cs.chipsLabel}>
              {perWeek
                ? t("composer.weeks", { defaultValue: "Weeks" })
                : t("composer.segments", { defaultValue: "Segments" })}
            </Text>
            <View style={cs.chipsRow}>
              {(perWeek ? Array.from({ length: weekCount }, (_, w) => w) : rampBlocks!).map(
                (item, i) => {
                  const on = i === ramp!.idx;
                  const locked = formDirty && !on;
                  const covered = !perWeek || rampWeeks!.includes(item as number);
                  const label = perWeek
                    ? `${covered ? "" : "＋"}W${(item as number) + 1}`
                    : rangeLabel("W", (item as (typeof rampBlocks)[number]).weeks);
                  return (
                    <Pressable
                      key={i}
                      accessibilityRole="button"
                      accessibilityState={{ selected: on, disabled: locked }}
                      disabled={locked}
                      onPress={() => selectRampSegment(i)}
                      hitSlop={8}
                      style={[
                        cs.chip,
                        on && cs.chipOn,
                        !covered && !on && { borderStyle: "dashed" },
                        locked && { opacity: 0.4 },
                      ]}
                    >
                      <Text style={[cs.chipText, on && cs.chipTextOn]}>{label}</Text>
                    </Pressable>
                  );
                },
              )}
              <Pressable
                accessibilityRole="button"
                accessibilityState={{ selected: perWeek, disabled: formDirty }}
                disabled={formDirty}
                onPress={() => setRampPerWeek(!perWeek)}
                hitSlop={8}
                style={[cs.chip, perWeek && cs.chipOn, formDirty && { opacity: 0.4 }]}
              >
                <Text style={[cs.chipText, perWeek && cs.chipTextOn]}>
                  {t("composer.perWeek", { defaultValue: "Per week" })}
                </Text>
              </Pressable>
            </View>
          </View>
          {formDirty ? (
            <Text style={cs.metaHint}>
              {t("composer.rampSwitchHint", {
                defaultValue: "Update or Cancel before switching segment",
              })}
            </Text>
          ) : null}
        </View>
      ) : null}

      {form.members.length > 0 ? (
        <ComposerSetTable
          members={form.members}
          grouped={form.grouped}
          onRemoveMember={removeMember}
          onReplaceMember={onReplaceMember}
          onUpdateRow={updateRow}
          onSetKind={setMemberKind}
          onSetInstructions={setMemberInstructions}
          onAddRound={addRound}
          onRemoveRound={removeRound}
          cellUnit={cellUnit}
          styles={cs}
          selectStyles={selectStyles}
          theme={theme}
        />
      ) : null}

      {/* Per-week edit pins day+week targets — only content edits. */}
      {weekEdit === null ? (
        <>
          <View style={cs.chipsLine}>
            <Text style={cs.chipsLabel}>{t("composer.days", { defaultValue: "Days" })}</Text>
            <View style={cs.chipsRow}>
              {Array.from({ length: dayCount }, (_, d) => {
                const on = form.days.has(d);
                return (
                  <Pressable
                    key={d}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    onPress={() => toggleDay(d)}
                    hitSlop={8}
                    style={[cs.chip, on && cs.chipOn]}
                  >
                    <Text style={[cs.chipText, on && cs.chipTextOn]}>
                      {on ? "✓ " : ""}D{d + 1}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          <View style={cs.chipsLine}>
            <Text style={cs.chipsLabel}>{t("composer.weeks", { defaultValue: "Weeks" })}</Text>
            <View style={cs.chipsRow}>
              {Array.from({ length: weekCount }, (_, w) => {
                const on = form.weeks.has(w);
                return (
                  <Pressable
                    key={w}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    onPress={() => toggleWeek(w)}
                    hitSlop={8}
                    style={[cs.chip, on && cs.chipOn]}
                  >
                    <Text style={[cs.chipText, on && cs.chipTextOn]}>
                      {on ? "✓ " : ""}W{w + 1}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </>
      ) : null}

      {formConflict ? (
        <Text style={cs.validationText}>
          {t("composer.dupExercise", {
            name: resolveName(formConflict.slug),
            day: formConflict.dayIdx + 1,
            defaultValue: "{{name}} is already on D{{day}} in the selected weeks",
          })}
        </Text>
      ) : null}

      <Pressable
        accessibilityRole="button"
        disabled={!canCommit}
        onPress={commit}
        style={[cs.commitBtn, !canCommit && cs.commitBtnDisabled]}
      >
        {editing ? (
          <Text style={[cs.commitBtnText, !canCommit && cs.commitBtnTextDisabled]}>
            {t("composer.update", { defaultValue: "Update" })}
          </Text>
        ) : (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <Icon name="plus" size={cs.commitBtnText.fontSize} color={!canCommit ? cs.commitBtnTextDisabled.color : cs.commitBtnText.color} />
            <Text style={[cs.commitBtnText, !canCommit && cs.commitBtnTextDisabled]}>
              {t("composer.add", { defaultValue: "Add" }).toUpperCase()}
            </Text>
          </View>
        )}
      </Pressable>

      {/* Secondary row: discard (quiet) + the copy-icon clone power-tool
          (brand, additive) sit side by side so the primary commit above and
          the danger remove below each read alone. */}
      <View style={{ flexDirection: "row", gap: 8 }}>
        <Pressable
          accessibilityRole="button"
          onPress={resetForm}
          style={[cs.cancelEditBtn, { flex: 1 }]}
        >
          <Text style={cs.cancelEditText}>{t("program.cancel", { defaultValue: "Cancel" })}</Text>
        </Pressable>
        {cloneable ? (
          <Pressable
            accessibilityRole="button"
            onPress={onClone}
            style={[cs.cancelEditBtn, { flex: 1 }]}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Icon name="copy" size={cs.statusActionText.fontSize} color={cs.statusActionText.color} />
              <Text style={cs.statusActionText}>{t("composer.clone", { defaultValue: "Clone" })}</Text>
            </View>
          </Pressable>
        ) : null}
      </View>

      {editing ? (
        <ConfirmRemoveButton
          onRemove={onRemove}
          style={ps.removeBtn}
          textStyle={ps.removeBtnText}
          confirmLabel={t("plans.removeExerciseConfirm", { defaultValue: "Remove this exercise?" })}
          removeLabel={t("plans.removeExercise", { defaultValue: "Remove exercise" })}
          confirmHint={t("plans.removeExerciseHint", { defaultValue: "Tap once to confirm, then tap again to remove." })}
        />
      ) : null}
    </View>
  );
}
